import React, { useEffect, useMemo, useRef, useState } from 'react';
import { confirmDiscardChanges, hasFormChanged } from '../../utils/unsavedChanges';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import PreviewPaper from '../prescriptions/PreviewPaper';
import {
  listContracts, createContract, updateContract, deleteContract,
  getContractsHistory, listPatients, listContractTemplates,
  sendContractEmail, createContractSignLink, getContractSignatures
} from '../../services/contractsApi';
import { getProfessionals, getBranding } from '../../services/prescriptionsApi';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const getId = (o) => o?.id || o?._id;

function buildDasPartes(branding, f, professionals = []) {
  const paciente = f.patient_name || '[NOME_PACIENTE]';
  const cpf = f.patient_cpf ? `CPF ${f.patient_cpf}` : '[CPF_PACIENTE]';
  const rg = f.patient_rg ? `, RG ${f.patient_rg}` : '';
  const endPaciente = f.patient_address || '[ENDERECO_PACIENTE]';

  const hasRepresentative = Boolean(f.patient_is_minor || f.is_minor_or_dependent || f.legal_guardian_name);

  const guardianTypeLabel = {
    menor: 'responsável legal pelo(a) paciente menor',
    tutor: 'tutor(a)/guardião(ã) do(a) paciente',
    curador: 'curador(a) do(a) paciente',
    procurador: 'procurador(a)/representante do(a) paciente',
    incapaz: 'responsável legal pelo(a) paciente incapaz',
    acompanhante_autorizado: 'responsável/acompanhante autorizado(a)',
    outro: 'responsável/representante do(a) paciente',
  }[f.legal_guardian_type] || 'responsável/representante do(a) paciente';

  const contratanteTexto = hasRepresentative
    ? `${f.legal_guardian_name || '[RESPONSÁVEL / REPRESENTANTE]'}, CPF ${f.legal_guardian_cpf || '[CPF_RESPONSAVEL]'}, na qualidade de ${guardianTypeLabel} ${paciente}, paciente ${f.patient_cpf ? `CPF ${f.patient_cpf}` : ''}${rg}, residente e domiciliado(a) em ${endPaciente}`
    : `${paciente}, ${cpf}${rg}, residente e domiciliado(a) em ${endPaciente}`;

  const selectedProfessional = professionals.find((p) => getId(p) === f.professional_id);

  const dentista =
    selectedProfessional?.name ||
    branding?.doctor_name ||
    branding?.professional_name ||
    '[NOME_DENTISTA]';

  const especialidade =
    getProfessionalSpecialty(selectedProfessional) ||
    branding?.specialty ||
    '';

  const croNumber =
    selectedProfessional?.cro_number ||
    selectedProfessional?.cro ||
    branding?.cro_number ||
    '';

  const croState =
    selectedProfessional?.cro_state ||
    selectedProfessional?.cro_uf ||
    branding?.cro_state ||
    '';

  const cro = croNumber
    ? `CRO${croState ? `-${croState}` : ''} ${croNumber}`
    : '[CRO]';

  const endClinica = branding?.address || '[ENDERECO_CLINICA]';

  if ((f.contracting_type || 'profissional') === 'clinica') {
    const razaoSocial = branding?.clinic_legal_name || '[RAZÃO SOCIAL DA CLÍNICA]';
    const cnpj = branding?.clinic_cnpj || '[CNPJ]';

    return `DAS PARTES

CONTRATANTE: ${contratanteTexto}.

CONTRATADA: ${razaoSocial}, CNPJ nº ${cnpj}, com sede em ${endClinica}.

PROFISSIONAL RESPONSÁVEL: ${dentista}${especialidade ? `, ${especialidade}` : ''}, ${cro}, com atuação no endereço profissional acima indicado.`;
  }

  return `DAS PARTES

CONTRATANTE: ${contratanteTexto}.

CONTRATADO: ${dentista}${especialidade ? `, ${especialidade}` : ''}, ${cro}, com consultório localizado em ${endClinica}.`;
}

function getStatusLabel(status) {
  const map = {
    draft: 'Rascunho',
    signed: 'Assinado',
    revoked: 'Revogado',
    finalized: 'Finalizado',
    pending: 'Pendente',
  };

  return map[status] || status || '—';
}

function isContractSigned(item) {
  return item?.status === 'signed' || item?.signature_status === 'completed';
}

function getContractVersionNumber(item, allItems = []) {
  if (!item?.version_of) return 1;

  const sameChain = allItems.filter((doc) =>
    doc.id === item.version_of ||
    doc.version_of === item.version_of ||
    doc.version_of === item.id
  );

  return sameChain.length || 2;
}

function getOriginalContract(item, allItems = []) {
  if (!item?.version_of) return null;
  return allItems.find((doc) => getId(doc) === item.version_of) || null;
}

function getProfessionalSpecialty(prof) {
  return (
    prof?.specialty ||
    prof?.primary_specialty ||
    (Array.isArray(prof?.specialties) ? prof.specialties[0] : '') ||
    ''
  );
}

function buildContractBranding(baseBranding, clinicSettings, form, professionals = []) {
  const selectedProfessional =
    professionals.find((p) => getId(p) === form.professional_id) ||
    professionals.find((p) => p.is_main) ||
    null;

  const doctorName =
    selectedProfessional?.name ||
    clinicSettings?.professional_name ||
    baseBranding?.doctor_name ||
    '';

  const croNumber =
    selectedProfessional?.cro_number ||
    selectedProfessional?.cro ||
    clinicSettings?.cro_number ||
    baseBranding?.cro_number ||
    '';

  const croState =
    selectedProfessional?.cro_state ||
    selectedProfessional?.cro_uf ||
    clinicSettings?.cro_state ||
    baseBranding?.cro_state ||
    '';

  return {
    ...baseBranding,

    // branding salvo na aba Visual sempre tem prioridade
    clinic_name:
      baseBranding?.clinic_name ||
      clinicSettings?.clinic_name ||
      '',

    clinic_legal_name:
      baseBranding?.clinic_legal_name ||
      clinicSettings?.clinic_legal_name ||
      '',

    clinic_cnpj:
      baseBranding?.clinic_cnpj ||
      clinicSettings?.cnpj ||
      '',

    clinic_cpf:
      baseBranding?.clinic_cpf ||
      clinicSettings?.cpf ||
      '',  
    doctor_name: doctorName,
    doctor_cpf: selectedProfessional?.cpf || clinicSettings?.cpf || '',
    specialty: getProfessionalSpecialty(selectedProfessional) || baseBranding?.specialty || '',
    cro_number: croNumber && croState ? `${croState} ${croNumber}` : croNumber,
    cro_state: croState,
    cro_display: croNumber ? `CRO${croState ? `-${croState}` : ''} ${croNumber}` : '',
    address:
      baseBranding?.address ||
      clinicSettings?.address ||
      '',

    phone:
      baseBranding?.phone ||
      clinicSettings?.phone ||
      '',

    email:
      baseBranding?.email ||
      clinicSettings?.email ||
      '',

    logo_url:
      baseBranding?.logo_url ||
      '',

    primary_color:
      baseBranding?.primary_color ||
      clinicSettings?.primary_color ||
      '#7c3aed',
  };
}

function buildDynamicFieldsBlock(form) {
  const fields = Array.isArray(form.dynamic_fields) ? form.dynamic_fields : [];
  const values = form.dynamic_fields_values || {};
  const contractText = form.contract_text || '';

  const filledFields = fields.filter((field) => {
    const value = values[field.key];
    const key = field.key;
    const alreadyUsedInBody = contractText.includes(`{{${key}}}`);
    return !alreadyUsedInBody && value !== null && value !== undefined && String(value).trim() !== '';
  });

  if (!filledFields.length) return '';

  return filledFields.map((field, index) => {
    const label = String(field.label || '').replace(/^\d+\.\s*/, '');
    const value = String(values[field.key] || '').trim();

    return `${index + 1}. ${label}\n${value}`;
  }).join('\n\n');
}

function replaceContractVariables(text, form, branding = {}) {
  let result = text || '';

  const isMinor = Boolean(form.patient_is_minor || form.is_minor_or_dependent);

  const guardianTypeLabel = {
    menor: 'responsável legal pelo(a) paciente menor',
    tutor: 'tutor(a)/guardião(ã) do(a) paciente',
    curador: 'curador(a) do(a) paciente',
    procurador: 'procurador(a)/representante do(a) paciente',
    incapaz: 'responsável legal pelo(a) paciente incapaz',
    acompanhante_autorizado: 'responsável/acompanhante autorizado(a)',
    outro: 'responsável/representante do(a) paciente',
  }[form.legal_guardian_type] || 'responsável/representante do(a) paciente';

  if (isMinor) {
    result = result.replace(
      "Eu, {{PACIENTE_NOME_COMPLETO}}, brasileiro(a), {{PACIENTE_ESTADO_CIVIL}}, RG nº {{PACIENTE_RG}} – {{PACIENTE_RG_ORGAO}}, CPF nº {{PACIENTE_CPF}}, residente e domiciliado(a) em {{PACIENTE_ENDERECO}},",
      `Eu, ${form.legal_guardian_name || '[NOME DO RESPONSÁVEL LEGAL]'}, ${guardianTypeLabel} ${form.patient_name || '[NOME DO PACIENTE]'}, CPF nº ${form.patient_cpf || '[CPF DO PACIENTE]'}, residente e domiciliado(a) em ${form.patient_address || '[ENDEREÇO DO PACIENTE]'},`
    );

    result = result.replace(
      'Declaro, de forma voluntária, livre e esclarecida, por meio deste Termo de Consentimento, que',
      `Eu, ${form.legal_guardian_name || '[NOME DO RESPONSÁVEL LEGAL]'}, CPF nº ${form.legal_guardian_cpf || '[CPF DO RESPONSÁVEL LEGAL]'}, na qualidade de ${guardianTypeLabel} ${form.patient_name || '[NOME DO PACIENTE]'}, declaro, de forma voluntária, livre e esclarecida, por meio deste Termo de Consentimento, que`
    );

    result = result.replace(
      /revogar esta autorização/g,
      'revogar esta autorização, na qualidade de responsável legal'
    );
  }

  const contractingType = form.contracting_type || 'profissional';

  const pacienteTextoContratante = isMinor
    ? `${form.legal_guardian_name || '[RESPONSÁVEL / REPRESENTANTE]'}, CPF nº ${form.legal_guardian_cpf || ''}, na qualidade de ${guardianTypeLabel} ${form.patient_name || ''}`
    : `${form.patient_name || ''}, CPF nº ${form.patient_cpf || ''}, residente e domiciliado(a) em ${form.patient_address || ''}`;

  const contratadoTexto = contractingType === 'clinica'
    ? `${branding.clinic_legal_name || '[RAZÃO SOCIAL DA CLÍNICA]'}, CNPJ nº ${branding.clinic_cnpj || ''}, com sede em ${branding.address || ''}`
    : `${branding.doctor_name || '[NOME DO PROFISSIONAL]'}, CPF nº ${branding.doctor_cpf || ''}, ${branding.cro_display || ''}, com endereço profissional em ${branding.address || ''}`;

  const replacements = {
    '{{PACIENTE_NOME_COMPLETO}}': form.patient_name || '',
    '{{PACIENTE_ESTADO_CIVIL}}': form.patient_marital_status || form.marital_status || '',
    '{{PACIENTE_RG}}': form.patient_rg || '',
    '{{PACIENTE_RG_ORGAO}}': '',
    '{{PACIENTE_CPF}}': form.patient_cpf || '',
    '{{PACIENTE_ENDERECO}}': form.patient_address || '',

    '{{CLINICA_NOME}}': branding.clinic_name || form.clinic_name || '',
    '{{CLINICA_ENDERECO}}': branding.address || form.clinic_address || '',
    '{{CLINICA_TELEFONE}}': branding.phone || form.clinic_phone || '',
    '{{CLINICA_EMAIL}}': branding.email || form.clinic_email || '',

    '{{PROFISSIONAL_NOME}}': branding.doctor_name || '',
    '{{PROFISSIONAL_ESPECIALIDADE}}': branding.specialty || '',
    '{{PROFISSIONAL_CRO}}': branding.cro_display || '',
    '{{PROFISSIONAL_CRO_NUMERO}}': branding.cro_number || '',
    '{{PROFISSIONAL_CRO_UF}}': branding.cro_state || '',

    '{{PACIENTE_TEXTO_CONTRATANTE}}': pacienteTextoContratante,
    '{{CONTRATADO_TEXTO}}': contratadoTexto,
    '{{PROFISSIONAL_CPF}}': branding.doctor_cpf || '',
    '{{PROFISSIONAL_CRO}}': branding.cro_display || '',
    '{{CLINICA_RAZAO_SOCIAL}}': branding.clinic_legal_name || '',
    '{{CLINICA_CNPJ}}': branding.clinic_cnpj || '',    
  };

  Object.entries(replacements).forEach(([key, value]) => {
    result = result.replaceAll(key, value || '');
  });

  Object.entries(form.dynamic_fields_values || {}).forEach(([key, value]) => {
    result = result.replaceAll(`{{${key}}}`, String(value || ''));
  });

  return result;
}

function buildFinalContractText(form, branding = {}) {
  const filledCount = Object.values(form.dynamic_fields_values || {})
    .filter((v) => String(v || '').trim() !== '').length;

  let baseText = replaceContractVariables(form.contract_text || '', form, branding);

  baseText = baseText.replace(
    /nos itens 1 a 9/g,
    `nos itens 1 a ${Math.max(1, filledCount)}`
  );

  return [
    baseText,
    buildDynamicFieldsBlock(form),
  ].filter(Boolean).join('\n\n');
}

async function sha256HexFromBlob(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function ContractsManager({ patientId, openContractId }) {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = (permission) => permissions.has(permission);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [branding, setBranding] = useState({});
  const [clinicSettings, setClinicSettings] = useState({});

  const [showModal, setShowModal] = useState(false);
  const initialFormRef = useRef(null);
  const overlayMouseDownRef = useRef(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    template_id: '',
    patient_id: '',
    patient_name: '',
    patient_cpf: '',
    patient_rg: '',
    patient_address: '',
    title: '',
    contract_text: '',
    status: 'draft',
    issue_date: new Date().toISOString(),
    professional_id: '',
    contracting_type: 'profissional',
    dynamic_fields_values: {},
  });

  const [contactEmail, setContactEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const [signLink, setSignLink] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);

  // NOVO: assinatura do paciente (imagem + metadados)
  const [patientSig, setPatientSig] = useState(null);

  // NOVO: busca de paciente
  const [searchTerm, setSearchTerm] = useState('');

  const isEditing = useMemo(() => Boolean(getId(selected)), [selected]);

  const isLocked =
    form.status === 'signed' ||
    form.status === 'revoked' ||
    form.signature_status === 'completed';

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Se vier patientId, filtra por paciente; senão, lista tudo
      const contractsParams = patientId ? { patient_id: patientId } : undefined;

      const [c, h, p, t, profs, settingsRes] = await Promise.all([
        listContracts(contractsParams),
        getContractsHistory(50, 0),
        listPatients(100),
        listContractTemplates(),
        getProfessionals(),
        api.get('/clinic-settings'),
      ]);
      setItems(Array.isArray(c) ? c : []);
      setHistory(Array.isArray(h) ? h : []);
      setPatients(Array.isArray(p) ? p : []);
      setTemplates(Array.isArray(t) ? t : []);
      setProfessionals(Array.isArray(profs) ? profs : []);
      setClinicSettings(settingsRes?.data || {});

      // Carrega branding inicial (padrão)
      const b = await getBranding(null);
      setBranding(b || {});
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [patientId]);

  useEffect(() => {
    if (!openContractId) return;
    if (!Array.isArray(items) || !items.length) return;
    if (showModal) return;

    const alreadyOpenedKey = `contract-auto-opened-${openContractId}`;

    if (sessionStorage.getItem(alreadyOpenedKey)) return;

    const contract = items.find(
      (c) => String(getId(c)) === String(openContractId)
    );

    if (!contract) return;

    sessionStorage.setItem(alreadyOpenedKey, '1');

    openEdit(contract);
  }, [openContractId, items, showModal]);

  // Carrega branding quando muda o profissional selecionado
  useEffect(() => {
    if (!showModal) return; // só carrega se modal estiver aberto
    (async () => {
      try {
        const b = await getBranding(form.professional_id || null);
        setBranding(b || {});
      } catch (error) {
        console.error("Erro ao carregar branding:", error);
      }
    })();
  }, [form.professional_id, showModal]);

  const openNew = () => {
    setSelected(null);
    setForm({
      template_id: '', patient_id: '', patient_name: '', patient_cpf: '', patient_rg: '', patient_address: '',
      title: '', contract_text: '', status: 'draft', issue_date: new Date().toISOString(),
      professional_id: '',
      dynamic_fields_values: {},
    });
    setPatientSig(null);
    setContactEmail('');
    setSignLink('');
    setSearchTerm('');
    setShowModal(true);
  };

  const openEdit = async (item) => {
    setSelected(item);
    setForm({
      template_id: item.template_id || '',
      patient_id: item.patient_id || '',
      patient_name: item.patient_name || '',
      patient_cpf: item.patient_cpf || '',
      patient_rg: item.patient_rg || '',
      patient_address: item.patient_address || '',
      patient_marital_status: item.patient_marital_status || '',
      patient_is_minor: Boolean(item.patient_is_minor || item.is_minor_or_dependent),
      is_minor_or_dependent: Boolean(item.patient_is_minor || item.is_minor_or_dependent),
      legal_guardian_name: item.legal_guardian_name || '',
      legal_guardian_cpf: item.legal_guardian_cpf || '',
      legal_guardian_phone: item.legal_guardian_phone || '',
      legal_guardian_relationship: item.legal_guardian_relationship || '',    
      legal_guardian_type: item.legal_guardian_type || '',
      legal_guardian_basis: item.legal_guardian_basis || '',
      legal_guardian_notes: item.legal_guardian_notes || '',        
      title: item.title || '',
      contract_text: item.contract_text || '',
      status: item.status || 'draft',
      issue_date: item.issue_date || new Date().toISOString(),
      signed_at: item.signed_at || null,
      signature_validation_code: item.signature_validation_code || null,
      signed_by_name: item.signed_by_name || null,
      signed_by_cpf: item.signed_by_cpf || null,
      signed_ip: item.signed_ip || null,
      professional_id: item.professional_id || '',
      contracting_type: item.contracting_type || 'profissional',
      version_of: item.version_of || null,
      dynamic_fields: item.dynamic_fields || [],
      dynamic_fields_values: item.dynamic_fields_values || {},
      rendered_contract_text: item.rendered_contract_text || '',
    });
    setContactEmail('');
    setSignLink('');
    setPatientSig(null);
    setSearchTerm('');

    // carrega a assinatura mais recente (se houver)
    try {
      const list = await getContractSignatures(getId(item), true);
      if (Array.isArray(list) && list.length) {
        setPatientSig(list[0]); // latest
      }
    } catch (e) {
      // silencioso se não houver
      console.warn('sem assinatura ainda');
    }
    setShowModal(true);
  };

  const onClose = () => {
    setShowModal(false);
    setSelected(null);
    setSignLink('');
    setPatientSig(null);
  };
  const requestClose = () => {
    const isDirty = hasFormChanged(initialFormRef.current, form);
    if (confirmDiscardChanges(isDirty)) onClose();
  };

  useEffect(() => {
    if (showModal) initialFormRef.current = form;
  }, [showModal]);

  const handleOverlayMouseDown = (event) => {
    overlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayMouseUp = (event) => {
    if (overlayMouseDownRef.current && event.target === event.currentTarget) requestClose();
    overlayMouseDownRef.current = false;
  };

  const onChangePatient = (patientId) => {
    const p = patients.find((pp) => getId(pp) === patientId);
    setForm((f) => ({
      ...f,
      patient_id: patientId,
      patient_name: p?.full_name || '',
      patient_cpf: p?.cpf || '',
      patient_rg: p?.rg || '',
      patient_address: p?.address || '',
      patient_marital_status: p?.marital_status || '',
      patient_is_minor: Boolean(p?.is_minor_or_dependent),
      is_minor_or_dependent: Boolean(p?.is_minor_or_dependent),
      legal_guardian_name: p?.legal_guardian_name || '',
      legal_guardian_cpf: p?.legal_guardian_cpf || '',
      legal_guardian_phone: p?.legal_guardian_phone || '',
      legal_guardian_relationship: p?.legal_guardian_relationship || '',
      legal_guardian_type: p?.legal_guardian_type || '',
      legal_guardian_basis: p?.legal_guardian_basis || '',
      legal_guardian_notes: p?.legal_guardian_notes || '',      
    }));
  };

  // aplica template no texto
  useEffect(() => {
    if (!form.template_id) return;
    const t = templates.find((tt) => getId(tt) === form.template_id);
    if (!t) return;

    let compiled = '';
    if (t.introduction_text) compiled += `${t.introduction_text}\n\n`;
    if (Array.isArray(t.clauses) && t.clauses.length > 0) {
      compiled += 'CLÁUSULAS:\n\n';
      t.clauses.forEach((c) => {
        const num = c.clause_number ? `${c.clause_number}.` : '';
        const title = c.title || 'Cláusula';
        const content = c.content || '';
        compiled += `${num}${title}\n${content}\n\n`;
      });
    }
    if (t.conclusion_text) compiled += `${t.conclusion_text}\n`;

    setForm(f => ({
      ...f,
      title: t.title || t.template_name || f.title || '',
      contract_text: compiled || f.contract_text || '',
      dynamic_fields: t.dynamic_fields || [],
      dynamic_fields_values: f.dynamic_fields_values || {},
    }));
  }, [form.template_id, templates]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.patient_id || !form.patient_name) {
      toast.error('Selecione o paciente (nome obrigatório).');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        dynamic_fields: Array.isArray(form.dynamic_fields) ? form.dynamic_fields : [],
        dynamic_fields_values: form.dynamic_fields_values || {},
        rendered_contract_text: buildFinalContractText(form, buildContractBranding(branding, clinicSettings, form, professionals)),
      };
      if (isEditing) {
        await updateContract(getId(selected), payload);
        toast.success('Contrato atualizado!');
      } else {
        await createContract(payload);
        toast.success('Contrato criado!');
      }
      onClose();
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao salvar contrato');
    } finally { setLoading(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Excluir este contrato?')) return;
    setLoading(true);
    try {
      await deleteContract(id);
      toast.success('Contrato excluído');
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao excluir contrato');
    } finally { setLoading(false); }
  };

  const pageRef = useRef(null);

  const onGeneratePDF = async () => {
    try {
      setLoading(true);
      const pdf = await buildPdf({ scale: 1.8, jpegQuality: 0.9 });
      const fileName = `contrato_${form.patient_name || 'doc'}.pdf`;
      let pdfBlob = pdf.output('blob');

      // 1️⃣ calcula hash do PDF original
      const pdfSha256 = await sha256HexFromBlob(pdfBlob);

      // 2️⃣ gera novo PDF já com o hash impresso
      const pdf2 = await buildPdf({ scale: 2, jpegQuality: 0.95 });

      const pageWidth = pdf2.internal.pageSize.getWidth();
      const pageHeight = pdf2.internal.pageSize.getHeight();

      pdf2.setPage(pdf2.getNumberOfPages());

      pdf2.setFontSize(8); // ↑ aumenta legibilidade
      pdf2.setTextColor(120); // cinza leve, estilo watermark técnico

      pdf2.text(
        `Hash SHA-256: ${pdfSha256}`,
        22,                 // ← afastado da borda
        pageHeight - 60,    // ↓ melhor alinhamento vertical
        {
          angle: 90,
        }
      );

      pdf2.setTextColor(0);

      // 3️⃣ usa o PDF final (sem recalcular hash de novo)
      pdfBlob = pdf2.output('blob');

      const fileUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();

      if (isEditing && getId(selected)) {
        await api.put(`/contracts/documents/${getId(selected)}/integrity`, {
          pdf_sha256: pdfSha256,
          validation_code: patientSig?.validation_code || form.signature_validation_code || '',
        });

        const pdfBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = String(reader.result || '');
            resolve(result.split(',')[1] || '');
          };
          reader.onerror = reject;
          reader.readAsDataURL(pdfBlob);
        });

        await api.post(`/contracts/documents/${getId(selected)}/signed-pdf`, {
          pdf_base64: pdfBase64,
          pdf_sha256: pdfSha256,
        });
      }

      toast.success('PDF gerado com hash de integridade registrado.');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao gerar PDF');
    } finally {
      setLoading(false);
    }
  };

  const buildPdf = async ({ scale = 2, jpegQuality = 0.95 } = {}) => {
    const node = pageRef.current;
    if (!node) throw new Error('Pré-visualização não encontrada');

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const pageNodes = Array.from(node.querySelectorAll('.a4-page'));
    if (pageNodes.length === 0) throw new Error('Nenhuma página A4 encontrada');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    for (let i = 0; i < pageNodes.length; i += 1) {
      const page = pageNodes[i];

      const canvas = await html2canvas(page, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/jpeg', jpegQuality);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      if (i > 0) pdf.addPage();

      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
    }

    const validationCode =
      patientSig?.validation_code || form.signature_validation_code || '';

    const signedAt =
      patientSig?.signed_at || form.signed_at || '';

    const signerIp =
      patientSig?.signer_ip || form.signed_ip || '';

    if (validationCode) {
      const validationUrl = `${window.location.origin}/public/validar-documento/${validationCode}`;

      const qrDataUrl = await QRCode.toDataURL(validationUrl, {
        width: 180,
        margin: 1,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.setPage(pdf.getNumberOfPages());

      // QR
      pdf.addImage(qrDataUrl, 'PNG', pageWidth - 80, pageHeight - 118, 60, 60);

      pdf.setFontSize(8);

      pdf.textWithLink('Validar', pageWidth - 75, pageHeight - 48, {
        url: validationUrl,
      });

      pdf.text(validationCode, pageWidth - 75, pageHeight - 38);

    }

    return pdf;
  };
  const onSendEmail = async () => {
    if (!contactEmail) return toast.warning('Digite o email');
    setSendingEmail(true);
    try {
      let pdf = await buildPdf({ scale: 1.6, jpegQuality: 0.85 });
      let base64 = pdf.output('datauristring').split(',')[1];

      await sendContractEmail({
        document_id: getId(selected) || 'temp',
        email: contactEmail,
        subject: `Contrato - ${form.title}`,
        message: 'Segue em anexo o contrato.',
        pdf_base64: base64,
        pdf_filename: `contrato_${form.patient_name}.pdf`
      });
      toast.success('Email enviado com sucesso!');
      setContactEmail('');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao enviar email');
    } finally { setSendingEmail(false); }
  };

  const onCreateSignLink = async () => {
    setCreatingLink(true);
    try {
      if (isEditing && getId(selected) && !isLocked) {
        await updateContract(getId(selected), {
          ...form,
          dynamic_fields: form.dynamic_fields || [],
          dynamic_fields_values: form.dynamic_fields_values || {},
          rendered_contract_text: buildFinalContractText(form, buildContractBranding(branding, clinicSettings, form, professionals)),
        });
      }      
      const res = await createContractSignLink(getId(selected));
      const url = res.sign_url;

      setSignLink(url);

      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Link de assinatura criado e copiado!');
      } else {
        toast.success('Link de assinatura criado!');
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao criar link');
    } finally {
      setCreatingLink(false);
    }
  };

  const revokeContractFromList = async (item) => {
    if (!item || !getId(item)) return;

    const reason = window.prompt(
      'Informe o motivo da revogação do consentimento:'
    );

    if (reason === null) return;

    const trimmedReason = String(reason || '').trim();

    if (!trimmedReason) {
      toast.warning('Informe um motivo para registrar a revogação.');
      return;
    }

    const confirmed = window.confirm(
      'Confirmar a revogação deste consentimento? O documento assinado será preservado, mas passará a constar como revogado.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      const res = await api.post(`/contracts/documents/${getId(item)}/revoke`, {
        reason: trimmedReason,
      });

      // cria automaticamente o Termo de Revogação

      try {
        const revokeTemplate = templates.find(
          (t) =>
            t.system_key === 'revogacao_consentimento_v1'
        );

        if (revokeTemplate) {
          const dynamicValues = {
            documento_revogado: item.title || '',
            data_documento_original: item.created_at
              ? new Date(item.created_at).toLocaleDateString()
              : '',
            procedimento_relacionado: '',
            motivo_revogacao: trimmedReason,
            ciencia_consequencias: '',
            observacoes: '',
          };

          await createContract({
            template_id: getId(revokeTemplate),
            patient_id: item.patient_id,
            patient_name: item.patient_name,
            patient_cpf: item.patient_cpf,
            patient_rg: item.patient_rg,
            patient_address: item.patient_address,

            patient_is_minor: item.patient_is_minor,
            is_minor_or_dependent: item.is_minor_or_dependent,

            legal_guardian_name: item.legal_guardian_name,
            legal_guardian_cpf: item.legal_guardian_cpf,
            legal_guardian_phone: item.legal_guardian_phone,
            legal_guardian_relationship: item.legal_guardian_relationship,
            legal_guardian_type: item.legal_guardian_type,
            legal_guardian_basis: item.legal_guardian_basis,

            professional_id: item.professional_id,

            title: revokeTemplate.title,
            contract_text: '',

            dynamic_fields: revokeTemplate.dynamic_fields || [],
            dynamic_fields_values: dynamicValues,
          });
        }
      } catch (e) {
        console.warn('Não foi possível gerar automaticamente o termo de revogação', e);
      }

      setItems((prev) =>
        prev.map((doc) =>
          getId(doc) === getId(item)
            ? res.data
            : doc
        )
      );

      await fetchAll();

      toast.success(
        'Consentimento revogado e termo de revogação criado.'
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao revogar consentimento.');
    } finally {
      setLoading(false);
    }
  };

  const refreshSignatureFromList = async (item) => {
    if (!item || !getId(item)) return;

    try {
      setLoading(true);

      const list = await getContractSignatures(getId(item), true);

      if (!Array.isArray(list) || !list.length) {
        toast.info('Assinatura ainda não recebida.');
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      await fetchAll();

      toast.success('Assinatura atualizada.');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao atualizar assinatura.');
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedSignature = async () => {
    if (!selected || !getId(selected)) return;

    try {
      const list = await getContractSignatures(getId(selected), true);

      if (Array.isArray(list) && list.length) {
        const sig = list[0];

        setPatientSig(sig);

        setForm((f) => ({
          ...f,
          status: 'signed',
          signed_at: sig.signed_at || f.signed_at,
          signed_by_name: sig.signer_name || f.signed_by_name,
          signed_by_cpf: sig.signer_cpf || f.signed_by_cpf,
          signed_ip: sig.signer_ip || f.signed_ip,
          signature_validation_code: sig.validation_code || f.signature_validation_code,
          signature_status: 'completed',
        }));

        await fetchAll();
        toast.success('Assinatura atualizada.');
      } else {
        toast.info('Assinatura ainda não recebida.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erro ao atualizar assinatura.');
    }
  };

  const copyLink = () => {
    if (!signLink) return;
    navigator.clipboard.writeText(signLink);
    toast.success('Link copiado!');
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Gerenciador de Contratos</h2>
        {can('contracts.create') && <button onClick={openNew} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          + Novo Contrato
        </button>}
      </div>

      {loading && <p>Carregando...</p>}

      {!loading && items.length === 0 && <p className="text-gray-500">Nenhum contrato encontrado.</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={getId(item)} className="bg-white p-4 rounded shadow border border-gray-200">
            <h3 className="font-bold text-lg">{item.title || 'Sem título'}</h3>
            <p className="text-sm text-gray-600">Paciente: {item.patient_name}</p>
            <p className="text-xs text-gray-400">Status: {getStatusLabel(item.status)}</p>
            <p className="text-xs text-gray-400">
              Versão: v{getContractVersionNumber(item, items)}
              {item.version_of ? ' — criada a partir de versão anterior' : ' — original'}
            </p>            
            <p className="text-xs text-gray-400">Data: {new Date(item.created_at).toLocaleDateString()}</p>
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => openEdit(item)} className="text-blue-600 text-sm hover:underline">
                {can('contracts.update') ? 'Abrir / Editar' : 'Abrir'}
              </button>

              {can('contracts.update') && item.status === 'signed' && (
                <button
                  onClick={() => revokeContractFromList(item)}
                  className="text-red-700 text-sm hover:underline"
                >
                  Revogar consentimento
                </button>
              )}

              {can('contracts.create') && isContractSigned(item) && (
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post(`/contracts/documents/${getId(item)}/new-version`);
                      toast.success('Nova versão criada!');
                      openEdit(res.data);
                      await fetchAll();
                    } catch (e) {
                      console.error(e);
                      toast.error('Erro ao criar nova versão');
                    }
                  }}
                  className="text-orange-600 text-sm hover:underline"
                >
                  Nova versão
                </button>
              )}

              {can('contracts.update') && item.status !== 'revoked' && (
                <button
                  onClick={() => refreshSignatureFromList(item)}
                  className="text-green-600 text-sm hover:underline"
                >
                  Atualizar assinatura
                </button>
              )}

              {can('contracts.send') && !isContractSigned(item) && item.status !== 'revoked' && (
                <button
                  onClick={async () => {
                    try {
                      const res = await createContractSignLink(getId(item));
                      if (res?.sign_url) {
                        await navigator.clipboard.writeText(res.sign_url);
                        toast.success('Link de assinatura criado e copiado!');
                      } else {
                        toast.success('Link de assinatura criado!');
                      }
                      await fetchAll();
                    } catch (e) {
                      console.error(e);
                      toast.error(e?.response?.data?.detail || 'Erro ao criar link');
                    }
                  }}
                  className="text-purple-600 text-sm hover:underline"
                >
                  Gerar link
                </button>
              )}

              {can('contracts.delete') && <button onClick={() => removeItem(getId(item))} className="text-red-600 text-sm hover:underline">
                Excluir
              </button>}
              {item.version_of && (
                <p className="text-xs text-orange-600 w-full">
                  Versão derivada do contrato original.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* MODAL DE EDIÇÃO / CRIAÇÃO */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayMouseUp}
        >
          <div
            className="bg-white w-full max-w-5xl rounded shadow-lg flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">
                {isEditing ? 'Editar Contrato' : 'Novo Contrato'}
              </h3>
              {isLocked && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  {form.status === 'revoked'
                    ? 'Documento revogado e bloqueado para edição.'
                    : 'Documento assinado e bloqueado para edição. Para alterar, crie uma nova versão.'}
                </div>
              )}            
              <button type="button" onClick={requestClose} className="text-gray-500 hover:text-gray-700">X</button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col md:flex-row gap-6">
              {/* ESQUERDA: FORMULÁRIO */}
              <div className="w-full md:w-1/3 flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Paciente</label>

                  {/* Busca de Paciente */}
                  <input
                    type="text"
                    placeholder="Buscar paciente..."
                    disabled={isLocked}
                    className="w-full border p-2 rounded mb-1 text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <select
                    className="w-full border p-2 rounded"
                    value={form.patient_id}
                    disabled={isLocked}
                    onChange={(e) => onChangePatient(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {patients
                      .filter(p => p.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((p) => (
                        <option key={getId(p)} value={getId(p)}>{p.full_name}</option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Template</label>
                  <select
                    className="w-full border p-2 rounded"
                    value={form.template_id}
                    disabled={isLocked}
                    onChange={(e) => setForm({ ...form, template_id: e.target.value })}
                  >
                    <option value="">(Nenhum / Personalizado)</option>
                    {templates.map((t) => (
                      <option key={getId(t)} value={getId(t)}>{t.template_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Profissional Responsável</label>
                  <select
                    className="w-full border p-2 rounded"
                    value={form.professional_id || ''}
                    disabled={isLocked}
                    onChange={(e) => setForm({ ...form, professional_id: e.target.value })}
                  >
                    <option value="">Padrão da Clínica</option>
                    {professionals.map((p) => (
                      <option key={getId(p)} value={getId(p)}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Define o layout (logo, cores) e dados do contratado.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Contratado no contrato
                  </label>
                  <select
                    className="w-full border p-2 rounded"
                    value={form.contracting_type || 'profissional'}
                    disabled={isLocked}
                    onChange={(e) => setForm({ ...form, contracting_type: e.target.value })}
                  >
                    <option value="profissional">Profissional</option>
                    <option value="clinica">Clínica</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Define se o contrato será firmado com o cirurgião-dentista ou com a clínica.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Título do Contrato</label>
                  <input
                    type="text"
                    className="w-full border p-2 rounded"
                    value={form.title}
                    disabled={isLocked}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>

                {Array.isArray(form.dynamic_fields) && form.dynamic_fields.length > 0 && (
                  <div className="border rounded p-3 bg-amber-50 space-y-3">
                    <h4 className="font-bold text-sm text-amber-800">
                      Campos específicos deste documento
                    </h4>

                    {form.dynamic_fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-600 ml-1">*</span>}
                        </label>

                        {field.type === 'textarea' ? (
                          <textarea
                            className="w-full border p-2 rounded text-sm min-h-[80px]"
                            disabled={isLocked}
                            value={form.dynamic_fields_values?.[field.key] || ''}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                dynamic_fields_values: {
                                  ...(f.dynamic_fields_values || {}),
                                  [field.key]: e.target.value,
                                },
                              }))
                            }
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full border p-2 rounded text-sm"
                            disabled={isLocked}
                            value={form.dynamic_fields_values?.[field.key] || ''}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                dynamic_fields_values: {
                                  ...(f.dynamic_fields_values || {}),
                                  [field.key]: e.target.value,
                                },
                              }))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Texto do Contrato</label>
                  <textarea
                    className="w-full border p-2 rounded h-64 font-mono text-sm"
                    value={form.contract_text}
                    disabled={isLocked}
                    onChange={(e) => setForm({ ...form, contract_text: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Use <code>[[NB]]...[[/NB]]</code> para blocos não-quebráveis.
                  </p>
                </div>

                {isEditing && (
                  <div className="border-t pt-4 mt-2">
                    <h4 className="font-bold text-sm mb-2">Histórico de versões</h4>

                    <div className="text-xs text-gray-600 space-y-1">
                      <div>
                        <strong>Versão atual:</strong> v{getContractVersionNumber(form, items)}
                      </div>

                      {form.version_of ? (
                        <div className="text-orange-700">
                          Este documento foi criado a partir de uma versão anterior.
                        </div>
                      ) : (
                        <div className="text-gray-500">
                          Este é o documento original.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4 mt-2">
                  <h4 className="font-bold text-sm mb-2">Ações</h4>
                  <div className="flex gap-2 flex-wrap">
                    {!isLocked && (isEditing ? can('contracts.update') : can('contracts.create')) && (
                      <button onClick={save} disabled={loading} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        {loading ? 'Salvando...' : 'Salvar'}
                      </button>
                    )}
                    <button onClick={onGeneratePDF} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
                      Gerar PDF
                    </button>
                  </div>
                </div>

                {isEditing && (
                  <div className="border-t pt-4 mt-2">
                    <h4 className="font-bold text-sm mb-2">Assinatura Digital</h4>
                    {can('contracts.update') && <button
                      type="button"
                      onClick={refreshSelectedSignature}
                      className="mb-2 w-full px-3 py-1.5 rounded bg-gray-100 text-gray-700 text-sm hover:bg-gray-200"
                    >
                      Atualizar assinatura
                    </button>}

                    {/* Se já assinado */}
                    {form.status === 'signed' && (
                      <div className="bg-green-50 border border-green-200 p-2 rounded mb-2">
                        <p className="text-green-800 font-bold text-sm">Assinado!</p>
                        <p className="text-xs">Por: {form.signed_by_name}</p>
                        <p className="text-xs">Em: {new Date(form.signed_at).toLocaleString()}</p>
                      </div>
                    )}

                    {/* Link para assinar */}
                    {can('contracts.send') && !signLink && form.status !== 'signed' && (
                      <button onClick={onCreateSignLink} disabled={creatingLink} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 w-full mb-2">
                        {creatingLink ? 'Criando...' : 'Criar Link de Assinatura'}
                      </button>
                    )}
                    {signLink && (
                      <div className="bg-gray-100 p-2 rounded mb-2">
                        <p className="text-xs text-gray-600 mb-1">Link para o paciente:</p>
                        <div className="flex gap-1">
                          <input readOnly value={signLink} className="text-xs border p-1 flex-1" />
                          <button onClick={copyLink} className="text-xs bg-gray-200 px-2 rounded">Copiar</button>
                        </div>
                      </div>
                    )}

                    {/* Enviar por Email */}
                    {can('contracts.send') && <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-700">Enviar PDF por Email</label>
                      <div className="flex gap-1">
                        <input
                          type="email"
                          placeholder="email@paciente.com"
                          className="border p-1 text-sm flex-1 rounded"
                          value={contactEmail}
                          onChange={e => setContactEmail(e.target.value)}
                        />
                        <button onClick={onSendEmail} disabled={sendingEmail} className="bg-blue-600 text-white px-2 py-1 rounded text-xs">
                          {sendingEmail ? '...' : 'Enviar'}
                        </button>
                      </div>
                    </div>}
                  </div>
                )}
              </div>

              {/* DIREITA: PREVIEW */}
              <div className="w-full md:w-2/3 bg-gray-50 p-4 border rounded overflow-auto flex justify-center">
                <div>
                  <PreviewPaper
                    captureRef={pageRef}
                    branding={buildContractBranding(branding, clinicSettings, form, professionals)}
                    content={{
                      ...form,
                      guidance_text: buildFinalContractText(
                        form,
                        buildContractBranding(branding, clinicSettings, form, professionals)
                      ),
                      rendered_contract_text: buildFinalContractText(
                        form,
                        buildContractBranding(branding, clinicSettings, form, professionals)
                      ),
                      parties_block: buildDasPartes(
                        buildContractBranding(branding, clinicSettings, form, professionals),
                        form,
                        professionals
                      ),
                      patient_signature_url:
                        patientSig?.signature_data_url ||
                        patientSig?.image_data ||
                        patientSig?.signature_url ||
                        null,
                      signed_by_name: patientSig?.signer_name || form.signed_by_name || null,
                      signed_by_cpf: patientSig?.signer_cpf || form.signed_by_cpf || null,
                      signed_at: patientSig?.signed_at || form.signed_at || null,
                      signed_ip: patientSig?.signer_ip || form.signed_ip || null,
                      signature_validation_code:
                        patientSig?.validation_code || form.signature_validation_code || null,
                    }}
                    variant="contract"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
